/**
 * P1 — baked-vs-rendered `<head>` honesty rail.
 *
 * ## The defect this exists for
 *
 * `.dcs/seo.yaml` is the owner-approved, portal-managed source of truth for a
 * page's `<title>` and description, and {@link buildHeadTags} bakes it into the
 * per-route static HTML. But the SAME resolver is also reachable at runtime via
 * `useSEO().applyHead(overrides)`, and an `overrides.title` is used VERBATIM —
 * it does not go through `titleTemplate`. So a view that hardcodes
 * `applyHead({ title: 'Our Services' })` silently replaces the approved title
 * the instant the app boots.
 *
 * Measured on live production (2026-07-27, before the site-local fix):
 *
 * ```
 * route      baked (non-JS crawler)                                   runtime (Google + humans)
 * /          Rochester Hills Handyman, Repairs & Carpentry | Iron Oak  Iron Oak Contractors | Crafted Repairs …
 * /services  Handyman, Carpentry & Home Repairs in SE Michigan | …     Our Services
 * ```
 *
 * Two crawlers, two different sites. Nothing failed. Nothing logged. The
 * approved SEO decision (C-147 / Q-141=B) was live for AI crawlers and reverted
 * for Google — for the whole time since it shipped.
 *
 * ## What the rail asserts
 *
 * After the build, for every route the emitter wrote, the prerender browser is
 * already loading the page. This rail reads `document.title` and the effective
 * `meta[name=description]` from that SAME render and compares them to the values
 * baked into the file on disk. Any divergence names the route and BOTH values.
 *
 * ## Why the pass condition is stable (not a moving target)
 *
 * A site whose views call `applyHead()` with no title/description overrides
 * produces byte-identical tags at runtime and at build time — same
 * {@link buildHeadTags}, same `seo.yaml` (the plugin bakes it into
 * `__DCS_SEO__`). Zero divergence is therefore the *structural* outcome of
 * seo.yaml being the only writer, not a threshold someone has to keep tuning.
 *
 * ## Escape hatches (visible by construction)
 *
 * `mode` can be lowered to `warn`/`off` and individual routes can be listed in
 * `allow`, but both live in `.dcs/seo.yaml` (portal-owned, in git, reviewable)
 * or in an env var that prints in the build log — there is no silent way to
 * switch this off. Every allowed route is still logged.
 */

/** How a divergence is reported. */
export type HonestyMode = 'error' | 'warn' | 'off'

/** One route's baked and rendered head values. */
export interface HeadObservation {
  /** Route path as it appears in `pages.yaml` (e.g. `/services`). */
  route: string
  /** `<title>` as written into the emitted file (still HTML-escaped). */
  bakedTitle: string | null
  /** `document.title` after the app mounted. */
  runtimeTitle: string | null
  /** `meta[name=description]@content` as written into the emitted file. */
  bakedDescription: string | null
  /** The EFFECTIVE `meta[name=description]` in the rendered DOM (the last one). */
  runtimeDescription: string | null
  /** How many `meta[name=description]` tags the rendered DOM carried. */
  runtimeDescriptionCount?: number
}

/** A single baked≠rendered divergence. */
export interface HeadHonestyViolation {
  route: string
  field: 'title' | 'description'
  baked: string | null
  runtime: string | null
}

/** Configuration for the rail. */
export interface HeadHonestyOptions {
  /** `error` (default) fails the build, `warn` logs, `off` skips entirely. */
  mode?: HonestyMode
  /** Also compare the meta description (default `true`). */
  checkDescription?: boolean
  /**
   * Route paths exempted from the comparison. An exemption is still LOGGED, so
   * "who turned this off for which route" is answerable from the build output.
   */
  allow?: string[]
}

// -----------------------------------------------------------------------------
// Normalisation
// -----------------------------------------------------------------------------

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  copy: '©',
  reg: '®',
  trade: '™',
  rsquo: '’',
  lsquo: '‘',
  ldquo: '“',
  rdquo: '”',
}

/**
 * Decode the HTML character references the emitter can produce.
 *
 * This is NOT a general-purpose entity decoder — it covers the set
 * `spliceHeadHtml`'s `escapeAttr` emits (`& " < >`) plus numeric references and
 * the handful of named ones that appear in real CMS copy. Anything unrecognised
 * is left verbatim, which is the safe direction: an unknown entity can only ever
 * cause a *reported* divergence to be investigated, never a real one to be
 * silently swallowed.
 */
export function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (whole, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X'
      const code = Number.parseInt(isHex ? body.slice(2) : body.slice(1), isHex ? 16 : 10)
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole
      try {
        return String.fromCodePoint(code)
      } catch {
        return whole
      }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()]
    return named ?? whole
  })
}

/**
 * Put a baked (HTML-source) value and a rendered (DOM) value on the same
 * footing before comparing them.
 *
 * The three transforms exist because of three MEASURED false-positive
 * mechanisms, not from caution:
 *   - **entity decode** — the baked side carries `Repairs &amp; Carpentry`
 *     while `document.title` yields `Repairs & Carpentry`. Identical strings.
 *   - **whitespace collapse** — `document.title` is specified to strip and
 *     collapse ASCII whitespace; the emitted `<title>` keeps the author's.
 *   - **Unicode NFC** — a composed `é` and a decomposed `é` render identically
 *     and mean the same thing; only the byte sequence differs.
 *
 * Deliberately NOT normalised: case, punctuation, and typographic look-alikes
 * (`-` vs `—`, `'` vs `’`). Those are real content differences and a rail that
 * hides them is the vacuous-green shape this whole class of work exists to kill.
 */
export function normalizeHeadText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const decoded = decodeHtmlEntities(value)
  const collapsed = decoded.replace(/\s+/g, ' ').trim()
  return collapsed.normalize('NFC')
}

// -----------------------------------------------------------------------------
// Baked-side extraction
// -----------------------------------------------------------------------------

/** The baked `<title>` / description of an emitted document. */
export interface BakedHead {
  title: string | null
  description: string | null
}

/**
 * Read one attribute out of a tag, honouring the QUOTE DELIMITER.
 *
 * The naive form `/\battr\s*=\s*["']([\s\S]*?)["']/` terminates on the first
 * quote OR apostrophe, so `content="Rochester Hills' favorite venue"` reads back
 * as `Rochester Hills`. Measured 2026-07-27 on the candidate matrix: that bug
 * alone manufactured P1 "divergences" on every route whose approved description
 * contains an apostrophe (boogie-babies /, mi-handyman /, all 9 iron-oak blog
 * posts) — a rail that reds a build over its own parser is worse than no rail.
 */
export function readTagAttr(tag: string, attr: string): string | null {
  const re = new RegExp(`\\b${attr}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'=<>\`]+))`, 'i')
  const m = re.exec(tag)
  if (!m) return null
  return m[1] ?? m[2] ?? m[3] ?? ''
}

/**
 * Read the baked `<title>` and `meta[name=description]` out of an emitted file.
 *
 * Regex-based on purpose — it mirrors `spliceHeadHtml`, runs over the emitter's
 * own deterministic output, and keeps a heavy HTML parser out of the build.
 */
export function extractBakedHead(html: string): BakedHead {
  const titleMatch = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html)
  const title = titleMatch ? titleMatch[1] : null

  let description: string | null = null
  const metaRe = /<meta\b[^>]*>/gi
  let m: RegExpExecArray | null
  while ((m = metaRe.exec(html)) !== null) {
    const tag = m[0]
    const name = readTagAttr(tag, 'name')
    if (name?.toLowerCase() !== 'description') continue
    description = readTagAttr(tag, 'content') ?? ''
  }

  return { title, description }
}

// -----------------------------------------------------------------------------
// Comparison
// -----------------------------------------------------------------------------

/**
 * Compare every observation and return the divergences.
 *
 * A `null` on either side is meaningful and reported: a runtime that DELETES the
 * baked description is the same class of silent undoing as one that rewrites it.
 * The one exception is a route where BOTH sides are absent — nothing was claimed,
 * so nothing was undone.
 */
export function findHeadHonestyViolations(
  observations: HeadObservation[],
  options: HeadHonestyOptions = {}
): HeadHonestyViolation[] {
  const { checkDescription = true, allow = [] } = options
  const allowed = new Set(allow)
  const violations: HeadHonestyViolation[] = []

  for (const obs of observations) {
    if (allowed.has(obs.route)) continue

    const bakedTitle = normalizeHeadText(obs.bakedTitle)
    const runtimeTitle = normalizeHeadText(obs.runtimeTitle)
    if (!(bakedTitle === null && runtimeTitle === null) && bakedTitle !== runtimeTitle) {
      violations.push({ route: obs.route, field: 'title', baked: bakedTitle, runtime: runtimeTitle })
    }

    if (!checkDescription) continue
    const bakedDescription = normalizeHeadText(obs.bakedDescription)
    const runtimeDescription = normalizeHeadText(obs.runtimeDescription)
    if (
      !(bakedDescription === null && runtimeDescription === null) &&
      bakedDescription !== runtimeDescription
    ) {
      violations.push({
        route: obs.route,
        field: 'description',
        baked: bakedDescription,
        runtime: runtimeDescription,
      })
    }
  }

  return violations
}

/** `null` renders as an explicit marker so an absent tag never looks like `''`. */
function display(value: string | null): string {
  return value === null ? '(absent)' : JSON.stringify(value)
}

/**
 * Render the full report. Every violation names the ROUTE and BOTH values, so
 * the operator never has to reproduce the divergence to act on it — which is the
 * difference between a rail people fix and a rail people mute.
 */
export function formatHeadHonestyReport(violations: HeadHonestyViolation[]): string {
  const routes = new Set(violations.map((v) => v.route))
  const lines: string[] = [
    `[dcs-seo] head honesty: ${violations.length} divergence(s) across ${routes.size} route(s) — ` +
      `the BAKED <head> (what a non-JS AI crawler receives) does not match the RENDERED <head> ` +
      `(what Google and every human sees).`,
  ]
  for (const v of violations) {
    lines.push(`  ${v.route}  [${v.field}]`)
    lines.push(`      baked   : ${display(v.baked)}`)
    lines.push(`      rendered: ${display(v.runtime)}`)
  }
  lines.push(
    `  Fix: delete the hardcoded title/description from the route's ` +
      `applyHead({…}) call so .dcs/seo.yaml is the only writer. ` +
      `(applyHead's \`title\` override is used VERBATIM — it does NOT go through ` +
      `global.titleTemplate, which is why an override that "looks right" still diverges.)`
  )
  return lines.join('\n')
}

/** Thrown in `error` mode so the build goes red on a divergence. */
export class HeadHonestyError extends Error {
  readonly violations: HeadHonestyViolation[]
  constructor(violations: HeadHonestyViolation[]) {
    super(formatHeadHonestyReport(violations))
    this.name = 'HeadHonestyError'
    this.violations = violations
  }
}

// -----------------------------------------------------------------------------
// Mode resolution
// -----------------------------------------------------------------------------

/** Inputs that can set the rail's severity, in increasing precedence. */
export interface HonestyModeInput {
  /** Compiled-in default. */
  fallback?: HonestyMode
  /** The plugin option in `vite.config.ts`. */
  option?: HonestyMode | boolean
  /** The per-site escape hatch in `.dcs/seo.yaml`. */
  seoYaml?: HonestyMode | boolean
  /** The env override (e.g. `DCS_SEO_HEAD_HONESTY=warn`) — highest precedence. */
  env?: string | undefined
}

function coerceMode(value: HonestyMode | boolean | string | undefined): HonestyMode | undefined {
  if (value === undefined || value === null) return undefined
  if (value === true) return 'error'
  if (value === false) return 'off'
  const normalized = String(value).trim().toLowerCase()
  if (normalized === 'error' || normalized === 'fail' || normalized === 'strict') return 'error'
  if (normalized === 'warn' || normalized === 'warning' || normalized === 'report') return 'warn'
  if (normalized === 'off' || normalized === 'false' || normalized === 'none') return 'off'
  return undefined
}

/**
 * Resolve the effective severity.
 *
 * Precedence is env > seo.yaml > plugin option > default, i.e. the MOST
 * operator-visible signal wins. An unrecognised value is ignored rather than
 * treated as "off" — a typo'd escape hatch must never silently disable a gate.
 */
export function resolveHonestyMode(input: HonestyModeInput): HonestyMode {
  return (
    coerceMode(input.env) ??
    coerceMode(input.seoYaml) ??
    coerceMode(input.option) ??
    input.fallback ??
    'error'
  )
}

// -----------------------------------------------------------------------------
// Execution assertion (C-338) — "did this rail actually run?"
// -----------------------------------------------------------------------------

/**
 * What the render pass was able to observe, so the rail can prove it RAN.
 *
 * ## The defect this exists for
 *
 * C-334 shipped P1 with an environment-dependent silent skip: when
 * `createPlaywrightRenderer` found no playwright/browser it warned and returned
 * no observations, and the caller then *skipped the assert entirely* — so an
 * `error`-severity rail let the build pass having checked nothing. That is the
 * same shape as C-322 (the snapshot rail was vacuous for months because `gh` was
 * missing on the runner) and the exact class this campaign exists to close.
 *
 * A rail is only a gate if "it did not run" is itself a failure. In `error`
 * mode the three non-execution shapes below are hard failures; in `warn` mode
 * they are warnings that name exactly what was not checked.
 */
export interface HeadHonestyExecution {
  /** Did the build have a headless renderer at all? */
  rendererAvailable: boolean
  /** Routes that reached the renderer (the denominator). */
  eligibleRoutes: string[]
  /** Routes that produced a baked-vs-rendered observation (the numerator). */
  observedRoutes: string[]
  /** Routes that never reached the renderer, with why. */
  filteredRoutes: Array<{ route: string; reason: string }>
  /** Every route in the manifest, for the "0 of N" arithmetic. */
  totalRoutes: number
}

/** Why the rail did not run (empty ⇒ it ran). */
export function findHeadHonestyExecutionFaults(ex: HeadHonestyExecution): string[] {
  const faults: string[] = []
  if (!ex.rendererAvailable) {
    faults.push(
      'no headless renderer was available (playwright missing, or Chromium failed to launch), ' +
        'so NOT ONE route\'s rendered <head> was observed'
    )
  }
  if (ex.eligibleRoutes.length === 0) {
    const byReason = new Map<string, string[]>()
    for (const f of ex.filteredRoutes) {
      byReason.set(f.reason, [...(byReason.get(f.reason) ?? []), f.route])
    }
    const detail =
      [...byReason.entries()].map(([reason, routes]) => `${reason}: ${routes.join(', ')}`).join('; ') ||
      'the route manifest was empty'
    faults.push(
      `0 of ${ex.totalRoutes} route(s) were eligible for observation (${detail}), ` +
        'so the baked-vs-rendered comparison had nothing to compare'
    )
  } else {
    const observed = new Set(ex.observedRoutes)
    const unobserved = ex.eligibleRoutes.filter((r) => !observed.has(r))
    if (unobserved.length > 0) {
      faults.push(
        `${ex.observedRoutes.length} of ${ex.eligibleRoutes.length} eligible route(s) produced an ` +
          `observation; these were NOT checked: ${unobserved.join(', ')}`
      )
    }
  }
  return faults
}

/** Render the "this rail did not run" report — it names what went unchecked. */
export function formatHeadHonestyExecutionReport(
  ex: HeadHonestyExecution,
  faults: string[]
): string {
  const lines: string[] = [
    '[dcs-seo] head honesty DID NOT RUN — the baked/rendered <head> divergence class was ' +
      'NOT checked for this build:',
  ]
  for (const f of faults) lines.push(`  - ${f}`)
  lines.push(
    `  observed ${ex.observedRoutes.length} / eligible ${ex.eligibleRoutes.length} / ` +
      `manifest ${ex.totalRoutes} route(s); renderer ${ex.rendererAvailable ? 'available' : 'ABSENT'}`
  )
  lines.push(
    '  Fix: install the renderer (`pnpm add -D playwright && npx playwright install chromium`) ' +
      'BEFORE the build step, or lower this rail deliberately and visibly ' +
      '(DCS_SEO_HEAD_HONESTY=warn / .dcs/seo.yaml headHonesty.mode). A rail that silently ' +
      'does not run is the exact class this work exists to close.'
  )
  return lines.join('\n')
}

/** Thrown in `error` mode when the P1 rail could not run. */
export class HeadHonestyNotRunError extends Error {
  readonly execution: HeadHonestyExecution
  readonly faults: string[]
  constructor(execution: HeadHonestyExecution, faults: string[]) {
    super(formatHeadHonestyExecutionReport(execution, faults))
    this.name = 'HeadHonestyNotRunError'
    this.execution = execution
    this.faults = faults
  }
}

/**
 * Assert the rail RAN. Throws {@link HeadHonestyNotRunError} in `error` mode,
 * warns in `warn` mode. Returns `true` when the comparison is worth running
 * (i.e. there is at least one observation to compare).
 */
export function assertHeadHonestyExecuted(
  execution: HeadHonestyExecution,
  options: { mode: HonestyMode }
): boolean {
  if (options.mode === 'off') return false
  const faults = findHeadHonestyExecutionFaults(execution)
  if (faults.length === 0) return true

  const report = formatHeadHonestyExecutionReport(execution, faults)
  if (options.mode === 'warn') {
    console.warn(report)
    console.warn(
      '[dcs-seo] head honesty is in WARN mode; this build is NOT gated on the above ' +
        '(in error mode it would be RED).'
    )
    // Partial observations are still worth comparing — a warn-mode build should
    // report the divergences it CAN see, having already said what it cannot.
    return execution.observedRoutes.length > 0
  }
  throw new HeadHonestyNotRunError(execution, faults)
}

/**
 * Apply the rail: throw in `error` mode, log in `warn` mode, do nothing in
 * `off`. Returns the violations so callers can report counts.
 */
export function assertHeadHonesty(
  observations: HeadObservation[],
  options: HeadHonestyOptions & { mode: HonestyMode; debug?: boolean }
): HeadHonestyViolation[] {
  const { mode, allow = [], debug = false } = options
  if (mode === 'off') {
    console.warn(
      '[dcs-seo] head honesty is OFF for this build — the baked/rendered <title> ' +
        'divergence class is NOT being checked.'
    )
    return []
  }

  if (allow.length > 0) {
    console.warn(
      `[dcs-seo] head honesty: ${allow.length} route(s) EXEMPTED from the baked==rendered ` +
        `assert: ${allow.join(', ')}`
    )
  }

  const violations = findHeadHonestyViolations(observations, options)

  if (violations.length === 0) {
    console.log(
      `[dcs-seo] head honesty: ${observations.length} route(s) verified — baked <head> == rendered <head>`
    )
    return violations
  }

  if (mode === 'warn') {
    console.warn(formatHeadHonestyReport(violations))
    console.warn(
      '[dcs-seo] head honesty is in WARN mode; this build is NOT gated on the divergence above.'
    )
    return violations
  }

  if (debug) console.error(formatHeadHonestyReport(violations))
  throw new HeadHonestyError(violations)
}
